import React, { useState, useEffect } from 'react';
import { X, Lightbulb, CheckCircle, ArrowRight } from 'lucide-react';

/**
 * OnboardingBanner Component
 * Shows helpful tips and guidance for first-time wealth tracking users
 */
const OnboardingBanner = ({ snapshotCount, isDark, onDismiss }) => {
  const [dismissed, setDismissed] = useState(false);

  // Check if user has dismissed onboarding before
  useEffect(() => {
    const dismissedKey = `wealthTracking_onboarding_dismissed_${snapshotCount}`;
    const wasDismissed = localStorage.getItem(dismissedKey);
    if (wasDismissed) {
      setDismissed(true);
    }
  }, [snapshotCount]);

  const handleDismiss = () => {
    const dismissedKey = `wealthTracking_onboarding_dismissed_${snapshotCount}`;
    localStorage.setItem(dismissedKey, 'true');
    setDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  if (dismissed) {
    return null;
  }

  // Different tips based on snapshot count
  const tips = {
    0: {
      title: 'Welcome to Wealth Tracking! 👋',
      steps: [
        'Create your first monthly snapshot to get started',
        'Enter your account balances as of today',
        'Record any contributions or withdrawals made this month',
        'The system automatically calculates your market growth'
      ]
    },
    1: {
      title: 'Great start! Here\'s what to do next:',
      steps: [
        'Create your next snapshot at the end of this month',
        'You\'ll start seeing month-over-month trends and charts',
        'Track how contributions vs. market growth affect your wealth',
        'Set a monthly reminder to create snapshots consistently'
      ]
    },
    2: {
      title: 'You\'re building momentum! 📈',
      steps: [
        'Keep creating monthly snapshots for best results',
        'Use the Edit button to correct any data entry mistakes',
        'Review your Growth Attribution chart to see what\'s working',
        'Compare accounts to see which are performing best'
      ]
    }
  };

  const currentTip = snapshotCount <= 2 ? tips[snapshotCount] : null;

  if (!currentTip) {
    return null;
  }

  return (
    <div
      className={`rounded-lg border p-4 sm:p-5 mb-6 ${
        isDark
          ? 'bg-gradient-to-r from-blue-900/20 to-purple-900/20 border-blue-500/30'
          : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200'
      }`}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className={`p-2 rounded-full shrink-0 ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
          <Lightbulb className={`w-5 h-5 sm:w-6 sm:h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-3">
            <h4 className={`font-semibold text-sm sm:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {currentTip.title}
            </h4>
            <button
              onClick={handleDismiss}
              className={`p-1 rounded-lg transition-colors shrink-0 ${
                isDark
                  ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200'
                  : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
              }`}
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-2">
            {currentTip.steps.map((step, index) => (
              <div key={index} className="flex items-start gap-2 sm:gap-3">
                <CheckCircle className={`w-4 h-4 sm:w-5 sm:h-5 shrink-0 mt-0.5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {step}
                </p>
              </div>
            ))}
          </div>

          {snapshotCount === 0 && (
            <div className="mt-4 flex items-center gap-2 text-xs sm:text-sm">
              <ArrowRight className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              <span className={`font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                Tip: It only takes 2-3 minutes to create your first snapshot!
              </span>
            </div>
          )}

          {snapshotCount === 1 && (
            <div className="mt-4 flex items-center gap-2 text-xs sm:text-sm">
              <ArrowRight className={`w-4 h-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              <span className={`font-semibold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                Pro tip: Consistent monthly tracking gives you the most valuable insights!
              </span>
            </div>
          )}

          {snapshotCount === 2 && (
            <div className="mt-4 flex items-center gap-2 text-xs sm:text-sm">
              <ArrowRight className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
              <span className={`font-semibold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                You're now seeing real trends! Keep it up for long-term success! 🎉
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingBanner;
